import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button, Alert, Spin, Select,
} from 'antd';
import apiClient from '../../utils/axios';
import LifecycleFunnel from './LifecycleFunnel';
import LifecycleEditor from './LifecycleEditor';

const PERIOD_OPTIONS = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export default function LifecycleDetailPage() {
  const { key } = useParams();
  const navigate = useNavigate();

  const [lifecycle, setLifecycle] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [editorOpen, setEditorOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    apiClient.get('/iam/me').then(res => {
      const u = res.data;
      setIsAdmin(u.role === 'admin');
    }).catch(() => setIsAdmin(false));
  }, []);

  const loadDefinition = async () => {
    const res = await apiClient.get(`/lifecycles/${key}`);
    setLifecycle(res.data);
    return res.data;
  };

  const loadFunnel = async (def) => {
    const stages = def?.stages || lifecycle?.stages;
    if (!stages) return;
    const res = await apiClient.get(`/lifecycles/${key}/funnel`, { params: { period } });
    setFunnel(res.data);
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const def = await loadDefinition();
      await loadFunnel(def);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load lifecycle');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!lifecycle) return;
    setError(null);
    loadFunnel(lifecycle).catch(err =>
      setError(err.response?.data?.detail || err.message || 'Failed to load funnel')
    );
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaved = () => {
    setEditorOpen(false);
    load();
  };

  return (
    <div style={{
      background: 'var(--bg-primary)',
      borderRadius: 12,
      border: '1px solid var(--border-color)',
      padding: '1.5rem',
      gridColumn: '1 / -1',
    }}>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Spin spinning={loading}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Button
                type="text"
                size="small"
                style={{ color: 'var(--text-muted)', padding: 0 }}
                onClick={() => navigate('/lifecycles')}
              >
                ← Lifecycles
              </Button>
            </div>
            <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.25rem', fontWeight: 700 }}>
              {lifecycle?.name || key}
            </h2>
            {lifecycle?.description && (
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                {lifecycle.description}
              </p>
            )}
            {/*funnel?.total_count && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {funnel?.total_count?.toLocaleString()} contacts created in the last {period}
            </p>)*/}
            {funnel?.total_lifecycle_count && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              {funnel?.total_lifecycle_count?.toLocaleString()} contacts evaluated in the last {period}
            </p>)}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select
              value={period}
              onChange={setPeriod}
              options={PERIOD_OPTIONS}
              style={{ width: 140 }}
              size="small"
            />
            {isAdmin && (
              <Button size="small" onClick={() => setEditorOpen(true)}>
                Edit
              </Button>
            )}
          </div>
        </div>

        {/* Funnel */}
        {lifecycle && funnel && (
          <LifecycleFunnel
            stages={lifecycle.stages}
            metrics={funnel}
          />
        )}

        {lifecycle && !funnel && !loading && (
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 13, textAlign: 'center' }}>
            No funnel data available yet.
          </p>
        )}
      </Spin>

      {isAdmin && (
        <LifecycleEditor
          open={editorOpen}
          lifecycle={lifecycle}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
